import type { App } from 'vue'

// Sends uncaught browser errors to POST /api/client-errors so they show up in
// Admin > Monitoring (server/src/models/clientErrorModel.ts). Production
// builds only; a page reports at most a handful, each message once, and a
// failure to report is swallowed so it can never cause a loop.

const MAX_REPORTS = 8
const sent = new Set<string>()

function describe(err: unknown): { message: string; stack: string } | null {
  if (!err) return null
  // HTTP failures are already visible on the server; stale-chunk errors
  // trigger a reload in main.ts. Neither is a bug in the page.
  const anyErr = err as any
  if (anyErr?.response || anyErr?.isAxiosError || typeof anyErr?.status === 'number') return null
  const message = err instanceof Error ? `${err.name}: ${err.message}` : typeof err === 'string' ? err : (() => {
    try { return JSON.stringify(err).slice(0, 300) } catch { return String(err) }
  })()
  if (/dynamically imported module|Importing a module script failed|Failed to fetch dynamically/i.test(message)) return null
  return { message, stack: err instanceof Error ? String(err.stack || '') : '' }
}

export function reportError(app: 'storefront' | 'admin', err: unknown, info?: string): void {
  if (!import.meta.env.PROD) return
  const d = describe(err)
  if (!d) return
  const message = info ? `${d.message} (${info})` : d.message
  if (sent.has(message) || sent.size >= MAX_REPORTS) return
  sent.add(message)
  try {
    fetch('/api/client-errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ app, message, stack: d.stack.slice(0, 4000), url: location.pathname }),
      keepalive: true,
    }).catch(() => {})
  } catch { /* never throw from the reporter */ }
}

export function installErrorReporting(vueApp: App, app: 'storefront' | 'admin'): void {
  const previous = vueApp.config.errorHandler
  vueApp.config.errorHandler = (err, instance, info) => {
    reportError(app, err, info)
    if (previous) previous(err, instance, info)
    else console.error(err)
  }
  window.addEventListener('error', event => reportError(app, event.error || event.message))
  window.addEventListener('unhandledrejection', event => reportError(app, event.reason))
}
